import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Calendar, Plus, Search, Edit, Trash2, Wrench, Clock, User, Play, CheckCircle, AlertCircle, List, Grid3X3 } from 'lucide-react';
import { DataTable } from '../../components/ui/data-table';

const PreventiveMaintenancePage = () => {
  const navigate = useNavigate();
  const { plants, selectedPlant, locations, selectedLocation } = useData();
  const [revisions, setRevisions] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [users, setUsers] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [activeView, setActiveView] = useState('cards');
  
  const [formData, setFormData] = useState({
    title: '',
    equipment_id: '',
    plant_id: '',
    location_id: '',
    scheduled_date: '',
    description: '',
    procedure_id: '',
    estimated_hours: '',
    assigned_to: ''
  });

  useEffect(() => {
    fetchData();
  }, [selectedPlant, selectedLocation]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { revision_type: 'preventive' };
      if (selectedPlant && selectedPlant !== 'all') params.plant_id = selectedPlant;
      if (selectedLocation && selectedLocation !== 'all') params.location_id = selectedLocation;
      
      const [revisionsRes, equipmentRes, usersRes, proceduresRes] = await Promise.all([
        api.get('/revisions', { params }),
        api.get('/equipment', { params: selectedPlant && selectedPlant !== 'all' ? { plant_id: selectedPlant } : {} }),
        api.get('/users'),
        api.get('/procedures')
      ]);
      
      setRevisions(revisionsRes.data);
      setEquipment(equipmentRes.data);
      setUsers(usersRes.data.filter(u => ['technician', 'manager'].includes(u.role)));
      setProcedures(proceduresRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        revision_type: 'preventive',
        scheduled_date: new Date(formData.scheduled_date).toISOString(),
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        tasks: [],
        consumables: []
      };
      
      await api.post('/revisions', payload);
      toast.success('Revizie preventiva creata!');
      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la creare');
    }
  };

  const handleStatusChange = async (revisionId, newStatus) => {
    try {
      await api.put(`/revisions/${revisionId}`, { status: newStatus });
      toast.success('Status actualizat!');
      fetchData();
    } catch (error) {
      toast.error('Eroare la actualizare');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa anulati aceasta revizie?')) return;
    try {
      await api.delete(`/revisions/${id}`);
      toast.success('Revizie anulata!');
      fetchData();
    } catch (error) {
      toast.error('Eroare la anulare');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      equipment_id: '',
      plant_id: selectedPlant !== 'all' ? selectedPlant : '',
      location_id: '',
      scheduled_date: '',
      description: '',
      procedure_id: '',
      estimated_hours: '',
      assigned_to: ''
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      planned: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-yellow-100 text-yellow-700',
      waiting_parts: 'bg-purple-100 text-purple-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-slate-100 text-slate-700'
    };
    const labels = {
      planned: 'Planificata',
      in_progress: 'In Lucru',
      waiting_parts: 'Asteapta Piese',
      completed: 'Finalizata',
      cancelled: 'Anulata'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{labels[status] || status}</Badge>;
  };

  const filteredRevisions = revisions.filter(r => {
    const matchesSearch = r.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          r.equipment_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || r.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // DataTable data
  const revisionsData = filteredRevisions.map(r => ({
    ...r,
    status_display: { planned: 'Planificata', in_progress: 'In Lucru', waiting_parts: 'Asteapta Piese', completed: 'Finalizata', cancelled: 'Anulata' }[r.status] || r.status,
    scheduled_display: new Date(r.scheduled_date).toLocaleDateString('ro-RO'),
    hours_display: r.estimated_hours ? `${r.estimated_hours}h` : '-'
  }));

  // DataTable columns  
  const tableColumns = [
    {
      key: 'title',
      label: 'Titlu',
      sortable: true,
      filterable: true,
      render: (value) => <span className="font-medium">{value}</span>
    },
    {
      key: 'equipment_name',
      label: 'Echipament',
      sortable: true,
      filterable: true
    },
    {
      key: 'status_display',
      label: 'Status',
      sortable: true,
      filterable: true,
      width: 'w-28',
      render: (value, row) => getStatusBadge(row.status)
    },
    {
      key: 'scheduled_display',
      label: 'Data Planificata',
      sortable: true,
      filterable: false,
      width: 'w-32'
    },
    {
      key: 'hours_display',
      label: 'Ore',
      sortable: true,
      filterable: false,
      width: 'w-20'
    },
    {
      key: 'assigned_to_name',
      label: 'Asignat la',
      sortable: true,
      filterable: true,
      render: (value) => value || <span className="text-slate-400">-</span>
    },
    {
      key: 'actions',
      label: 'Actiuni',
      sortable: false,
      filterable: false,
      width: 'w-40',
      render: (_, row) => (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          {row.status === 'planned' && (
            <Button size="sm" onClick={() => handleStatusChange(row.id, 'in_progress')} className="bg-green-600 hover:bg-green-700 h-8">
              <Play className="w-3 h-3 mr-1" />START
            </Button>
          )}
          {row.status === 'in_progress' && (
            <Button size="sm" onClick={() => handleStatusChange(row.id, 'completed')} className="bg-green-600 hover:bg-green-700 h-8">
              <CheckCircle className="w-3 h-3 mr-1" />FINAL
            </Button>
          )}
          {!['completed', 'cancelled'].includes(row.status) && (
            <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} className="text-red-600 h-8">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      )
    }
  ];

  const stats = {
    planned: revisions.filter(r => r.status === 'planned').length,
    in_progress: revisions.filter(r => r.status === 'in_progress').length,
    completed: revisions.filter(r => r.status === 'completed').length
  };

  return (
    <div className="space-y-6" data-testid="preventive-maintenance-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Mentenanta Preventiva</h1>
          <p className="text-slate-600 mt-1">Revizii planificate pentru echipamente</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="btn-primary h-12" data-testid="add-preventive-btn">
              <Plus className="w-5 h-5 mr-2" />
              REVIZIE NOUA
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Planifica Revizie Preventiva</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Titlu *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="h-12"
                  placeholder="ex: Revizie lunara linie A"
                  data-testid="preventive-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sediu *</Label>
                  <Select
                    value={formData.plant_id}
                    onValueChange={(value) => setFormData({ ...formData, plant_id: value, location_id: '' })}
                    required
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Selecteaza" />
                    </SelectTrigger>
                    <SelectContent>
                      {plants.map((plant) => (
                        <SelectItem key={plant.id} value={plant.id}>{plant.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Locatie *</Label>
                  <Select
                    value={formData.location_id}
                    onValueChange={(value) => setFormData({ ...formData, location_id: value })}
                    required
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Selecteaza" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.filter(l => !formData.plant_id || l.plant_id === formData.plant_id).map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Echipament *</Label>
                <Select
                  value={formData.equipment_id}
                  onValueChange={(value) => setFormData({ ...formData, equipment_id: value })}
                  required
                >
                  <SelectTrigger className="h-12" data-testid="preventive-equipment-select">
                    <SelectValue placeholder="Selecteaza echipamentul" />
                  </SelectTrigger>
                  <SelectContent>
                    {equipment.filter(e => !formData.plant_id || e.plant_id === formData.plant_id).map((eq) => (
                      <SelectItem key={eq.id} value={eq.id}>{eq.name} ({eq.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data planificata *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                    required
                    className="h-12"
                    data-testid="preventive-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ore estimate</Label>
                  <Input
                    type="number"
                    value={formData.estimated_hours}
                    onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })}
                    className="h-12"
                    min="0"
                    step="0.5"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Procedura</Label>
                <Select
                  value={formData.procedure_id}
                  onValueChange={(value) => setFormData({ ...formData, procedure_id: value })}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Selecteaza procedura (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {procedures.map((proc) => (
                      <SelectItem key={proc.id} value={proc.id}>{proc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Asignat la</Label>
                <Select
                  value={formData.assigned_to}
                  onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Selecteaza tehnician (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>{user.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Descriere</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descriere activitati..."
                  rows={3}
                />
              </div>
              <Button type="submit" className="w-full h-12 btn-primary" data-testid="save-preventive-btn">
                PLANIFICA REVIZIE
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="card-industrial border-blue-200 bg-blue-50">
          <CardContent className="p-4 text-center">
            <Calendar className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-700">{stats.planned}</p>
            <p className="text-sm text-blue-600">Planificate</p>
          </CardContent>
        </Card>
        <Card className="card-industrial border-yellow-200 bg-yellow-50">
          <CardContent className="p-4 text-center">
            <Play className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-yellow-700">{stats.in_progress}</p>
            <p className="text-sm text-yellow-600">In Lucru</p>
          </CardContent>
        </Card>
        <Card className="card-industrial border-green-200 bg-green-50">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-700">{stats.completed}</p>
            <p className="text-sm text-green-600">Finalizate</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Cauta revizie..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-12"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full md:w-48 h-12">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate</SelectItem>
            <SelectItem value="planned">Planificate</SelectItem>
            <SelectItem value="in_progress">In Lucru</SelectItem>
            <SelectItem value="waiting_parts">Asteapta Piese</SelectItem>
            <SelectItem value="completed">Finalizate</SelectItem>
          </SelectContent>
        </Select>
        <Tabs value={activeView} onValueChange={setActiveView}>
          <TabsList className="h-12">
            <TabsTrigger value="cards" className="px-3 h-10">
              <Grid3X3 className="w-4 h-4" />
            </TabsTrigger>
            <TabsTrigger value="table" className="px-3 h-10">
              <List className="w-4 h-4" />
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Revisions List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
        </div>
      ) : activeView === 'table' ? (
        <DataTable
          data={revisionsData}
          columns={tableColumns}
          pageSize={10}
          searchable={false}
          exportable={true}
          printable={true}
          selectable={true}
          onRowClick={(row) => navigate(`/revisions/${row.id}`)}
          emptyMessage="Nu exista revizii preventive"
        />
      ) : filteredRevisions.length > 0 ? (
        <div className="space-y-3">
          {filteredRevisions.map((revision) => (
            <Card key={revision.id} className="card-industrial" data-testid={`preventive-card-${revision.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Wrench className="w-6 h-6 text-[#0052CC]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900">{revision.title}</p>
                        {getStatusBadge(revision.status)}
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{revision.equipment_name}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(revision.scheduled_date).toLocaleDateString('ro-RO')}
                        </span>
                        {revision.estimated_hours && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {revision.estimated_hours}h
                          </span>
                        )}
                        {revision.assigned_to_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {revision.assigned_to_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {revision.status === 'planned' && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusChange(revision.id, 'in_progress')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Play className="w-4 h-4 mr-1" />
                        START
                      </Button>
                    )}
                    {revision.status === 'in_progress' && (
                      <Button
                        size="sm"
                        onClick={() => handleStatusChange(revision.id, 'completed')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        FINALIZEAZA
                      </Button>
                    )}
                    {revision.status !== 'completed' && revision.status !== 'cancelled' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(revision.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="card-industrial">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg">Nu exista revizii preventive</p>
            <p className="text-slate-400 text-sm mt-1">Planifica prima revizie pentru a incepe</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PreventiveMaintenancePage;
